import React, { useEffect } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Award } from "lucide-react";

// Tamanhos em cqw (1% da largura do certificado): o desenho se ajusta sozinho à tela, ao celular e à impressão.
const u = (px) => `${px / 10}cqw`;

export const CERT_VARS = [
  ["aluno", "Nome do aluno"], ["cpf", "CPF do aluno"], ["curso", "Nome do curso"], ["carga_horaria", "Carga horária"],
  ["aulas", "Nº de aulas"], ["data_inicio", "Data de início"], ["data_conclusao", "Data de conclusão"], ["data_emissao", "Data de emissão"],
  ["frequencia", "Frequência"], ["media_final", "Média final"], ["nota", "Nota da prova (%)"],
  ["codigo", "Código de validação"], ["validade", "Validade"], ["coordenador", "Coordenador(a)"],
];

export const SAMPLE_FIELDS = {
  aluno: "Maria da Silva Santos", cpf: "000.000.000-00", curso: "EaD/Básico - Cabeleireiro Científico Formulador", carga_horaria: "36h19min", aulas: "40",
  data_emissao: new Date().toLocaleDateString("pt-BR"), data_inicio: "01/03/2026", data_conclusao: new Date().toLocaleDateString("pt-BR"),
  frequencia: "100%", media_final: "9.00", nota: "90%", codigo: "IEP-EXEMPLO1", validade: "", coordenador: "Nome do Coordenador",
  modulos: ["Módulo 1 de exemplo", "Módulo 2 de exemplo", "Módulo 3 de exemplo"],
};

/** Troca {{variavel}} pelo valor; os valores saem em negrito. */
export function fillText(text, fields) {
  return String(text || "").split(/(\{\{\w+\}\})/g).map((part, i) => {
    const m = part.match(/^\{\{(\w+)\}\}$/);
    if (!m) return part;
    const v = fields?.[m[1]];
    return v ? <strong key={i}>{v}</strong> : (CERT_VARS.some(([k]) => k === m[1]) ? "" : part);
  });
}

function useScriptFont(on) {
  useEffect(() => {
    if (!on || document.getElementById("cert-font-script")) return;
    const l = document.createElement("link");
    l.id = "cert-font-script"; l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Great+Vibes&family=Playfair+Display:ital,wght@1,500&display=swap";
    document.head.appendChild(l);
  }, [on]);
}

const Label = ({ children }) => <div style={{ fontSize: u(12), fontWeight: 700, letterSpacing: "0.04em", color: "#0F172A", textTransform: "uppercase" }}>{children}</div>;
const Val = ({ children }) => <div style={{ fontSize: u(13), color: "#1E293B", marginTop: u(4) }}>{children}</div>;

/** Verso: registro de conclusão (aluno, curso, datas, carga horária, conteúdo e registro). */
function Back({ t, f }) {
  const lines = String(t.back_registry || "").split("\n").filter((x) => x.trim());
  const mods = f.modulos || [];
  const cols = [["Início", f.data_inicio], ["Conclusão", f.data_conclusao], ["Carga horária", f.carga_horaria ? (/^\d+$/.test(String(f.carga_horaria)) ? `${f.carga_horaria} horas` : f.carga_horaria) : ""],
    ["Frequência", f.frequencia], ["Média final", f.media_final]].filter(([, v]) => v);
  return (
    <div className="relative bg-white overflow-hidden shadow-xl print:shadow-none" style={{ aspectRatio: "1.414/1", marginTop: u(24), breakBefore: "page" }}>
      <div style={{ padding: `${u(36)} ${u(40)}` }}>
        <div className="text-center" style={{ fontSize: u(22), color: "#0F172A", letterSpacing: "0.02em" }}>{t.back_title}</div>
        <div className="grid" style={{ gridTemplateColumns: "1.1fr 1.6fr 0.8fr", gap: u(24), marginTop: u(44) }}>
          <div><Label>Aluno</Label><Val>{f.aluno}</Val></div>
          <div><Label>Curso</Label><Val>{f.curso}</Val></div>
          <div><Label>Código</Label><Val>{f.codigo}</Val></div>
        </div>
        <div className="grid" style={{ gridTemplateColumns: `repeat(${Math.max(cols.length, 1)}, 1fr)`, gap: u(16), marginTop: u(34), textAlign: "center" }}>
          {cols.map(([l, v]) => <div key={l}><div style={{ fontSize: u(14), color: "#0F172A", fontFamily: "Georgia, serif" }}>{l}</div><Val>{v}</Val></div>)}
        </div>
        <div className="grid" style={{ gridTemplateColumns: "1.5fr 1fr", gap: u(30), marginTop: u(34) }}>
          <div>
            {mods.length > 0 && <>
              <div style={{ fontSize: u(14), color: "#0F172A", fontFamily: "Georgia, serif", marginBottom: u(10) }}>CONTEÚDO PROGRAMÁTICO</div>
              <ol style={{ fontSize: u(12), color: "#1E293B", lineHeight: 1.45, listStyle: "none", margin: 0, padding: 0 }}>
                {mods.map((m, i) => <li key={i}>{i + 1}. {m}</li>)}
              </ol>
            </>}
          </div>
          <div className="text-center">
            <div style={{ fontSize: u(14), color: "#0F172A", fontFamily: "Georgia, serif", marginBottom: u(10) }}>REGISTRO</div>
            <img src={t.logo_url || "/brand/logo.png"} alt="" style={{ height: u(60), width: "auto", margin: "0 auto" }} />
            <div style={{ fontSize: u(12), color: "#1E293B", lineHeight: 1.5, marginTop: u(8) }}>
              {lines.map((l, i) => <div key={i}>{l}</div>)}
            </div>
            {t.back_logo2_url && <img src={t.back_logo2_url} alt="" style={{ height: u(46), width: "auto", margin: `${u(10)} auto 0` }} />}
          </div>
        </div>
      </div>
      {t.show_qr !== false && f.codigo && (
        <div className="absolute flex flex-col items-center" style={{ bottom: u(18), left: u(30), gap: u(3) }}>
          <QRCodeCanvas value={`${window.location.origin}/validar/${f.codigo}`} size={200} level="M" style={{ width: u(64), height: u(64) }} />
          <span style={{ fontSize: u(9), color: "#94A3B8" }}>Validar online</span>
        </div>
      )}
    </div>
  );
}

/** Desenha o certificado (frente e verso) a partir de um modelo e dos dados do aluno. */
export default function CertificateView({ template, fields, className = "" }) {
  const t = template || {};
  const primary = t.primary_color || "#232D4F";
  const accent = t.accent_color || "#1E9E2A";
  const f = fields || {};
  useScriptFont(t.name_font === "cursiva" || t.name_font === "elegante");
  const sigs = (t.signatures || []).filter((s) => s.name || s.role || s.image_url);
  const bg = t.background_url;
  const dateText = f.data_emissao ? `Emitido em ${f.data_emissao}` : "";
  const arte = t.layout === "arte" && !!bg;
  const nameStyle = t.name_font === "elegante"
    ? { fontFamily: "'Playfair Display', Georgia, serif", fontStyle: "italic", fontWeight: 500, textTransform: "uppercase", fontSize: u(arte ? 40 : 44) }
    : t.name_font === "cursiva"
      ? { fontFamily: "'Great Vibes', cursive", fontWeight: 400, fontSize: u(58) }
      : { fontSize: u(46), fontWeight: 700 };
  return (
    <div className={className} style={{ containerType: "inline-size", width: "100%", maxWidth: 1000, margin: "0 auto" }}>
      <style>{"@media print { @page { size: A4 landscape; margin: 0 } }"}</style>
      <div id="certificate" className="relative bg-white overflow-hidden shadow-xl print:shadow-none"
        style={{ aspectRatio: "1.414/1", borderRadius: bg ? 0 : u(16), ...(bg ? { backgroundImage: `url(${bg})`, backgroundSize: "100% 100%", backgroundRepeat: "no-repeat" } : {}) }}>
        {!bg && <>
          <div className="absolute inset-0" style={{ border: `${u(10)} solid ${primary}`, borderRadius: u(16) }} />
          <div className="absolute" style={{ inset: u(10), border: `${u(2)} solid ${accent}`, borderRadius: u(12) }} />
          <div className="absolute top-0 left-0 right-0" style={{ height: u(8), background: `linear-gradient(90deg,${primary},${accent})` }} />
        </>}
        {arte ? (
          <div className="absolute text-center" style={{ left: u(50), right: u(50), top: u(172), color: t.text_color || "#000" }}>
            {t.intro && <p style={{ textAlign: "left", fontSize: u(21), lineHeight: 1.2, maxWidth: u(330), margin: 0 }}>{fillText(t.intro, f)}</p>}
            <h1 className="font-display" style={{ color: primary, marginTop: u(26), lineHeight: 1.15, ...nameStyle }}>{f.aluno}</h1>
            <p style={{ fontSize: u(23), marginTop: u(36), lineHeight: 1.38, padding: `0 ${u(6)}` }}>{fillText(t.body, f)}</p>
          </div>
        ) : (
        <div className="relative h-full flex flex-col items-center justify-center text-center" style={{ padding: `${u(40)} ${u(70)}` }}>
          {!bg && <img src={t.logo_url || "/brand/logo.png"} alt="" style={{ height: u(64), width: "auto", marginBottom: u(14) }} />}
          {t.title && (
            <div className="inline-flex items-center font-semibold uppercase" style={{ color: accent, fontSize: u(14), letterSpacing: "0.2em", gap: u(8) }}>
              <Award style={{ width: u(16), height: u(16) }} /> {t.title}
            </div>
          )}
          {t.intro && <p style={{ color: t.text_color || "#475569", fontSize: u(16), marginTop: u(22) }}>{fillText(t.intro, f)}</p>}
          <h1 className="font-display" style={{ color: primary, marginTop: u(8), lineHeight: 1.15, ...nameStyle }}>{f.aluno}</h1>
          <p style={{ color: t.text_color || "#475569", fontSize: u(16), marginTop: u(20), maxWidth: u(720), lineHeight: 1.6 }}>{fillText(t.body, f)}</p>

          <div className="flex items-end justify-center w-full" style={{ gap: u(50), marginTop: u(34) }}>
            {sigs.map((sg, i) => (
              <div key={i} className="text-center" style={{ width: u(210) }}>
                <div style={{ height: u(44), display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
                  {sg.image_url && <img src={sg.image_url} alt="" style={{ maxHeight: u(44), maxWidth: "100%" }} />}
                </div>
                <div style={{ borderTop: `${u(2)} solid #CBD5E1`, paddingTop: u(6), fontSize: u(12), color: "#64748B" }}>
                  {sg.name && <div style={{ color: "#334155", fontWeight: 600 }}>{sg.name}</div>}
                  {sg.role}
                </div>
              </div>
            ))}
            {dateText && (
              <div className="text-center" style={{ width: u(210) }}>
                <div style={{ height: u(44) }} />
                <div style={{ borderTop: `${u(2)} solid #CBD5E1`, paddingTop: u(6), fontSize: u(12), color: "#64748B" }}>{dateText}</div>
              </div>
            )}
          </div>

          {(t.show_code !== false || t.footer_text) && (
            <div className="absolute left-0 right-0 text-center font-mono" style={{ bottom: u(22), color: "#94A3B8", fontSize: u(11) }}>
              {t.footer_text && <div style={{ marginBottom: u(2) }}>{fillText(t.footer_text, f)}</div>}
              {t.show_code !== false && <>Código de validação: {f.codigo}{f.validade ? ` · Válido até ${f.validade}` : ""}</>}
            </div>
          )}
          {t.show_qr !== false && f.codigo && !t.back_enabled && (
            <div className="absolute flex flex-col items-center" style={{ bottom: u(22), right: u(30), gap: u(4) }}>
              <QRCodeCanvas value={`${window.location.origin}/validar/${f.codigo}`} size={200} fgColor={primary} level="M" style={{ width: u(70), height: u(70) }} />
              <span style={{ fontSize: u(9), color: "#94A3B8" }}>Validar online</span>
            </div>
          )}
        </div>
        )}
        {arte && t.show_code !== false && (
          <div className="absolute font-mono" style={{ left: u(30), bottom: u(24), color: "#CBD5E1", fontSize: u(11) }}>
            Código de validação: {f.codigo}{f.validade ? ` · Válido até ${f.validade}` : ""}
          </div>
        )}
      </div>
      {t.back_enabled && <Back t={t} f={f} />}
    </div>
  );
}
